import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Seo } from "@/components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ = {
  es: [
    ["¿Dónde está el gym Frenchies en Coronado?", "En el Coronado Padel Center, Nueva Gorgona (Coronado, Panamá Oeste), a minutos de Coronado, Gorgona y San Carlos."],
    ["¿Cuál es el horario del gym?", "Abrimos todos los días de 7:00 a 19:00. Clases grupales de lunes a viernes a las 7:00, 8:30 y 18:00, sábados a las 9:00. Clases privadas y Pilates en todo el horario."],
    ["¿Qué tipo de entrenamiento ofrecen?", "Functional movement (movimiento funcional), fitness funcional, calistenia, fuerza, Pilates Reformer y coaching privado, además de programas para niños y adolescentes (Baby, Kids, Teens)."],
    ["¿Cómo reservo una clase?", "Crea tu cuenta, compra un pack y reserva desde el horario en pocos toques."],
    ["¿Puedo cancelar una reserva?", "Sí, según la política del estudio. Verás el plazo y el impacto en tus créditos antes de confirmar."],
    ["¿Qué pasa si la clase está llena?", "Puedes unirte a la lista de espera y te avisamos si se libera un lugar."],
    ["¿Qué métodos de pago aceptan?", "Pago en línea con tarjeta a través de la app (Paguelo Fácil), con factura electrónica DGI."],
    ["¿Necesito experiencia para empezar?", "No. Cada clase se adapta a tu nivel (Rookie, Fitness, Legend). Puedes empezar con una clase suelta de prueba."],
  ],
  en: [
    ["Where is the Frenchies gym in Coronado?", "Inside the Coronado Padel Center, Nueva Gorgona (Coronado, Panamá Oeste), minutes from Coronado, Gorgona and San Carlos."],
    ["What are the gym's opening hours?", "Open every day 7:00–19:00. Group classes Monday to Friday at 7:00, 8:30 and 18:00, Saturdays at 9:00. Private classes and Pilates across opening hours."],
    ["What kind of training do you offer?", "Functional movement, functional fitness, calisthenics, strength, Pilates Reformer and private coaching, plus youth programs (Baby, Kids, Teens)."],
    ["How do I book a class?", "Create your account, buy a pack and book from the schedule in a few taps."],
    ["Can I cancel a booking?", "Yes, according to the studio policy. You'll see the deadline and credit impact before confirming."],
    ["What if the class is full?", "You can join the waitlist and we'll notify you if a spot opens."],
    ["Which payment methods do you accept?", "Online card payment through the app (Paguelo Fácil), with DGI electronic invoice."],
    ["Do I need experience to start?", "No. Every class is scaled to your level (Rookie, Fitness, Legend). Start with a drop-in trial class."],
  ],
  fr: [
    ["Où se trouve le gym Frenchies à Coronado ?", "Au Coronado Padel Center, Nueva Gorgona (Coronado, Panamá Oeste), à quelques minutes de Coronado, Gorgona et San Carlos."],
    ["Quels sont les horaires du gym ?", "Ouvert tous les jours de 7h à 19h. Cours collectifs du lundi au vendredi à 7h, 8h30 et 18h, samedi à 9h. Cours privés et Pilates sur toute l'amplitude."],
    ["Quel type d'entraînement proposez-vous ?", "Functional movement, fitness fonctionnel, calisthénie, force, Pilates Reformer et coaching privé, plus des programmes enfants et ados (Baby, Kids, Teens)."],
    ["Comment réserver un cours ?", "Crée ton compte, achète un pack et réserve depuis le planning en quelques clics."],
    ["Puis-je annuler une réservation ?", "Oui, selon la politique du studio. Tu verras le délai et l'impact sur tes crédits avant de confirmer."],
    ["Que se passe-t-il si le cours est complet ?", "Tu peux rejoindre la liste d'attente et nous te prévenons si une place se libère."],
    ["Quels moyens de paiement acceptez-vous ?", "Paiement en ligne par carte via l'application (Paguelo Fácil), avec facture électronique DGI."],
    ["Faut-il de l'expérience pour commencer ?", "Non. Chaque cours est adapté à ton niveau (Rookie, Fitness, Legend). Commence par un cours d'essai à l'unité."],
  ],
};

const FAQ_LD = {
  "@type": "FAQPage",
  mainEntity: FAQ.es.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
};
const CRUMBS = [{ name: "FAQ", path: "/faq" }];

export default function FAQ_Page() {
  const { t, lang } = useI18n();
  const items = FAQ[lang] || FAQ.es;
  return (
    <PublicLayout>
      <Seo
        title="Preguntas frecuentes — Gym Frenchies Fitness & Pilates Coronado"
        description="Horarios, ubicación, tipos de clases, reservas, cancelaciones y pagos del gym Frenchies Fitness & Pilates en Coronado, Panamá."
        path="/faq"
        keywords="preguntas frecuentes gym Coronado, horario gym Coronado, reservar clase Coronado, Frenchies Fitness FAQ"
        jsonLd={FAQ_LD}
        breadcrumbs={CRUMBS}
      />
      <section className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
        <h1 className="mb-8 font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("nav.faq")}</h1>
        <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-5">
          {items.map(([q, a], i) => (
            <AccordionItem key={q} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-display">{q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </PublicLayout>
  );
}
